import React from 'react'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { MoreHorizontal, Play, Trash2, Settings, Pencil } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { Program, ProgramStatus } from './program.types'

interface ProgramsTableProps {
  programs: Program[]
  loading: boolean
  onView?: (program: Program) => void
  onEdit?: (program: Program) => void
  onDelete?: (program: Program) => void
  onManage?: (program: Program) => void
}

const getEnableBadge = (isEnabled: boolean) => {
  if (isEnabled) {
    return <Badge className="bg-green-500 text-background">เปิดใช้งาน</Badge>
  }
  return <Badge className="bg-red-500 text-background">ปิดใช้งาน</Badge>
}

const getStatusBadge = (status: ProgramStatus) => {
  if (status === ProgramStatus.PENDING) {
    return <Badge className="bg-yellow-500 text-white">กำลังรอ</Badge>
  }
  if (status === ProgramStatus.LIVE) {
    return <Badge className="bg-red-500 text-white">รายการสด</Badge>
  }
  return <Badge className="bg-gray-500 text-white">สิ้นสุดแล้ว</Badge>
}

export default function ProgramsTable({
  programs: initialPrograms,
  loading,
  onEdit,
  onDelete,
  onManage,
}: ProgramsTableProps) {
  const [programs, setPrograms] = useState<Program[]>(initialPrograms)
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)

  // Update programs when initialPrograms changes
  React.useEffect(() => {
    setPrograms(initialPrograms)
  }, [initialPrograms])

  if (loading) {
    return (
      <Card className="p-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">รูปภาพ</TableHead>
                <TableHead>ข้อมูลโปรแกรม</TableHead>
                <TableHead className="w-24">สถานะ</TableHead>
                <TableHead className="w-24">การเผยแพร่</TableHead>
                <TableHead className="w-16">การจัดการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div className="w-12 h-12 bg-gray-200 rounded-lg animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded animate-pulse" />
                      <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4" />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-8 w-8 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    )
  }

  if (programs.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <p className="text-base text-mute">ไม่พบข้อมูลโปรแกรม</p>
      </div>
    )
  }

  return (
    <Card className="p-0">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">รูปภาพ</TableHead>
              <TableHead>ข้อมูลโปรแกรม</TableHead>
              <TableHead className="w-24">สถานะ</TableHead>
              <TableHead className="w-24">การเผยแพร่</TableHead>
              <TableHead className="w-16">การจัดการ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {programs.map((program) => (
              <TableRow key={program.id}>
                <TableCell>
                  <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                    {program.thumbnailUrl ? (
                      <img
                        src={program.thumbnailUrl}
                        alt={program.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Play className="h-6 w-6 text-gray-400" />
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <div className="space-y-1">
                      <p className="text-base font-medium text-black">
                        {program.title}
                      </p>
                      <p className="text-sm text-mute">
                        เจ้าของ:{' '}
                        {program?.owner?.displayName ||
                          program?.owner?.firstName ||
                          program?.ownerId ||
                          '-'}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>{getEnableBadge(program.isEnabled)}</TableCell>
                <TableCell>{getStatusBadge(program.status)}</TableCell>
                <TableCell className="text-right">
                  <DropdownMenu
                    open={openDropdownId === program.id}
                    onOpenChange={(open) => {
                      setOpenDropdownId(open ? program.id : null)
                    }}
                  >
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {onEdit && (
                        <DropdownMenuItem onClick={() => onEdit(program)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          แก้ไข
                        </DropdownMenuItem>
                      )}
                      {onManage && (
                        <DropdownMenuItem onClick={() => onManage(program)}>
                          <Settings className="mr-2 h-4 w-4" />
                          จัดการ
                        </DropdownMenuItem>
                      )}
                      {onDelete && (
                        <DropdownMenuItem
                          onClick={() => onDelete(program)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          ลบ
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
